/**
 * How the trips of one day are named and grouped, for every component that
 * shows them. One place, so the list, the timeline, the inspector and the
 * findings panel never disagree about which trip is "Trip 2".
 *
 * There are no fixed AM/PM shifts (spec section 5, planner rule change of 27
 * September 2026): a duty starts where it fits within the milking windows. So
 * a trip is named "Trip n" for its vehicle on its day, numbered by departure.
 * The API's `shift` and `trip_seq` are not used for this: `shift` is exactly
 * the AM/PM block the spec rules out, and `trip_seq` is the planner's own
 * number within a shift, not within the vehicle's day.
 */
import type { Trip, Vehicle } from "./api";

/** The vehicle's registration, or which cab it is when none is recorded. */
export function vehicleName(vehicle: Vehicle): string {
  if (vehicle.registration) return vehicle.registration;
  if (vehicle.cab_unit_id !== null) return `Cab ${vehicle.cab_unit_id}`;
  return "Unknown vehicle";
}

/** What makes two trips the same vehicle: the cab, then the registration. A
 *  trip with neither is a vehicle of its own rather than merged with others. */
function vehicleKey(trip: Trip): string {
  if (trip.vehicle.cab_unit_id !== null) return `cab:${trip.vehicle.cab_unit_id}`;
  if (trip.vehicle.registration) return `reg:${trip.vehicle.registration}`;
  return `route:${trip.route_id}`;
}

/** By departure instant (not by string: offsets may differ), a trip without a
 *  departure last, and the route id to settle a tie the same way every time. */
function byDeparture(a: Trip, b: Trip): number {
  const at = a.departure === null ? Infinity : Date.parse(a.departure);
  const bt = b.departure === null ? Infinity : Date.parse(b.departure);
  if (at !== bt) return at < bt ? -1 : 1;
  return a.route_id - b.route_id;
}

export interface VehicleRow {
  key: string;
  name: string;
  vehicle: Vehicle;
  /** This vehicle's trips on the day, by departure. */
  trips: Trip[];
}

/** One row per vehicle, in order of each vehicle's first departure. */
export function vehicleRows(trips: Trip[]): VehicleRow[] {
  const rows = new Map<string, VehicleRow>();
  for (const trip of [...trips].sort(byDeparture)) {
    const key = vehicleKey(trip);
    let row = rows.get(key);
    if (!row) {
      row = { key, name: vehicleName(trip.vehicle), vehicle: trip.vehicle, trips: [] };
      rows.set(key, row);
    }
    row.trips.push(trip);
  }
  return [...rows.values()];
}

/** "Trip n" for every route id on the day. */
export function tripLabels(trips: Trip[]): Map<number, string> {
  const labels = new Map<number, string>();
  for (const row of vehicleRows(trips)) {
    row.trips.forEach((trip, index) => labels.set(trip.route_id, `Trip ${index + 1}`));
  }
  return labels;
}

/** Every trip of the day in the order the list and the tab order use: by
 *  vehicle row, then by departure, the same order the timeline draws. */
export function tripsInOrder(trips: Trip[]): Trip[] {
  return vehicleRows(trips).flatMap((row) => row.trips);
}

/** "Trip 1 · WILLIS" for a route id on the day, or null when the day holds no
 *  such trip. What a finding links to, in the panel and in Needs attention. */
export function tripTitle(trips: Trip[], routeId: number | null): string | null {
  if (routeId === null) return null;
  const trip = trips.find((t) => t.route_id === routeId);
  return trip ? `${tripLabels(trips).get(routeId)} · ${vehicleName(trip.vehicle)}` : null;
}
